import { useQuery } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { sessionStore } from 'src/store/sessionStore';

export interface ClientOverviewResponse {
  status: string;
  data: {
    overview: {
      approach: string;
      goalApproach?: string;
      frequency: number;
      week: number | string;
      sessionTime: number;
      equipment: string[];
      programId?: string | null;
      programName?: string;
      primaryGoal?: string;
      isDraft?: boolean;
      status?: string;
      generatorType?: string;
    };
    thisWeek: {
      workoutsCompleted: number;
      totalWorkouts: number;
      duration: number;
      trainingVolume: string;
      completionRate: string;
      completionRateNum?: number;
    };
    adherence?: {
      workoutCompletion: string;
      workoutCompletionNum: number;
      avgTimeAdherence: string;
      avgTimeAdherenceNum: number;
      planRespect: string;
      planRespectNum: number;
      planRespectTotal: number;
    };
    recentActivity: {
      lastCompleted: string | null;
      lastCheckin?: string | null;
      nextScheduled: string | null;
    };
    aiObservations: {
      id: string;
      text: string;
      date: string;
    }[];
    myNotes: {
      id: string;
      title: string;
      description: string;
      date: string;
      pinned: boolean;
      pin_time: string | null;
    }[];
  };
}

const getClientOverview = async (
  clientId: string,
  trainerId?: string,
): Promise<ClientOverviewResponse['data']> => {
  if (!clientId) {
    throw new Error('clientId is required');
  }

  let apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CLIENT_OVERVIEW}?clientId=${clientId}`;
  if (trainerId) {
    apiUrl += `&trainerId=${trainerId}`;
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch client overview');
  }

  const res: ClientOverviewResponse = await response.json();
  return res.data;
};

export const useGetClientOverview = (clientId: string) => {
  const { currentUser } = sessionStore();
  const trainerId = currentUser?.uid;

  return useQuery({
    queryKey: ['clientOverview', clientId, trainerId],
    queryFn: () => getClientOverview(clientId, trainerId),
    enabled: !!clientId,
  });
};
